import { Auth } from '@genesislcap/foundation-comms';
import { customElement, GenesisElement, observable, volatile } from '@genesislcap/web-core';
import { PlatformActivityStyles as styles } from './platform-activity.styles';
import { PlatformActivityTemplate as template } from './platform-activity.template';
import { GridApi } from '@ag-grid-community/core';

@customElement({
  name: 'platform-activity-route',
  template,
  styles,
})
export class PlatformActivityRoute extends GenesisElement {
  @Auth private auth: Auth;
  @observable public bondDetailsOpen: boolean = false;
  @observable selectedBondId: string | null = null;
  @observable selectedBondCusip: string | null = null;
  @observable filterValue: string = '';
  @observable cusipFilterValue: string = '';
  @observable referenceFilterValue: string = '';
  @observable issuerFilterValue: string = '';
  @observable counterpartyFilterValue: string = '';
  @observable todaysActivityGridStateKey: string = 'platform-activity-grid-state';
  @observable bondsFilterCriteria: string = '';
  @observable bidsFilterCriteria: string = 'BOND_ID==null';
  @observable bidAuditHistoryFilterCriteria: string = 'BOND_ID==null';
  private bondsGridApi: GridApi | null = null;
  private statusRefreshInterval: any = null;

  @volatile
  get bondsGridOptions() {
    return {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      getRowId: (params) => String(params.data?.BOND_ID ?? '').trim(),
      rowSelection: 'single',
      onGridReady: (params) => {
        this.bondsGridApi = params.api;
        this.statusRefreshInterval = setInterval(() => {
          if (this.bondsGridApi) {
              this.bondsGridApi?.refreshCells({ columns: ['STATUS'], force: true });
            }
          }, 60000);
      },
      onRowClicked: (event) => {
        this.handleBondRowClick(event);
      },
      onModelUpdated: (params) => {
        this.handleBondsModelUpdated(params);
      },
    };
  }

  @volatile
  get bidsGridOptions() {
    return {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      rowSelection: 'single',
      suppressRowClickSelection: true,
    };
  }

  @volatile
  get bidAuditHistoryGridOptions() {
    return {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      rowSelection: 'none',
      suppressRowClickSelection: true,
    };
  }

  handleBondRowClick(event) {
    // Handle bond row click events
    console.log('Bond row clicked:', event.data);
    this.selectedBondId = event.data?.BOND_ID || null;
    this.selectedBondCusip = event.data?.CUSIP || null;
    
    // Update the bids filter criteria to only show bids for the selected bond
    if (this.selectedBondId) {
      this.bidsFilterCriteria = `BOND_ID == "${this.selectedBondId}"`;
      this.bidAuditHistoryFilterCriteria = `BOND_ID == "${this.selectedBondId}"`;
    } else {
      this.bidsFilterCriteria = '';
      this.bidAuditHistoryFilterCriteria = '';
    }

    // Select the clicked row
    if (event.node) {
      event.node.setSelected(true);
    }
  }

  handleBondsModelUpdated(params) {
    if (!this.selectedBondId) {
      const firstRow = params.api.getDisplayedRowAtIndex(0);
      if (firstRow) {
        // Use setTimeout to ensure grid is fully rendered
        setTimeout(() => {
          firstRow.setSelected(true);
          this.handleBondRowClick({ data: firstRow.data, node: firstRow });
        }, 100);
      }
    }
  }

  handleCusipKeyUp(event) {
    // Allow Enter key to trigger filter
    if (event.key === 'Enter') {
      this.handleFilterApply();
    }
  }

  handleCusipInputChange(event) {
    const value = event.target.value;
    this.cusipFilterValue = value;
    
    // Automatically remove filter if content is completely deleted
    if (!value || value.trim() === '') {
      this.bondsFilterCriteria = '';
    }
  }

  handleReferenceKeyUp(event) {
    // Allow Enter key to trigger filter
    if (event.key === 'Enter') {
      this.handleFilterApply();
    }
  }

  handleReferenceInputChange(event) {
    const value = event.target.value;
    this.referenceFilterValue = value;
    
    // Automatically remove filter if content is completely deleted
    if (!value || value.trim() === '') {
      this.bondsFilterCriteria = '';
    }
  }

  handleIssuerKeyUp(event) {
    // Allow Enter key to trigger filter
    if (event.key === 'Enter') {
      this.handleFilterApply();
    }
  }

  handleIssuerInputChange(event) {
    const value = event.target.value;
    this.issuerFilterValue = value;
    
    // Automatically remove filter if content is completely deleted
    if (!value || value.trim() === '') {
      this.bondsFilterCriteria = '';
    }
  }

  handleCounterpartyKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.handleFilterApply();
    }
  }

  handleCounterpartyInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.counterpartyFilterValue = target.value;
  }

  handleFilterApply() {
    const cusipValue = this.cusipFilterValue?.trim();
    const referenceValue = this.referenceFilterValue?.trim();
    const issuerValue = this.issuerFilterValue?.trim();
    const counterpartyValue = this.counterpartyFilterValue?.trim();
    this.applyFilters(cusipValue, referenceValue, issuerValue, counterpartyValue);
  }

  private applyFilters(
    cusipValue: string,
    referenceValue: string,
    issuerValue: string,
    counterpartyValue: string
  ) {
    // Build filter criteria dynamically
    const filters = [];
    
    if (cusipValue) {
      filters.push(`CUSIP == "${cusipValue.toUpperCase()}"`);
    }
    if (referenceValue) {
      filters.push(`BOND_ID == "${referenceValue}"`);
    }
    if (issuerValue) {
      filters.push(`ISSUER_NAME == "${issuerValue}"`);
    }
    
    if (counterpartyValue) {
      filters.push(`COUNTERPARTY_IDS.containsIgnoreCase("${counterpartyValue}")`);
    }
    
    // Combine all filters with AND operator
    const filterCriteria = filters.join(' && ');
    
    // Update the bonds filter criteria
    this.bondsFilterCriteria = filterCriteria;
    
    // Clear current selection to trigger new selection when data loads
    this.selectedBondId = null;
    this.selectedBondCusip = null;
    this.bidsFilterCriteria = 'BOND_ID==null';
    this.bidAuditHistoryFilterCriteria = 'BOND_ID==null';
    
    // Use setTimeout to ensure the grid has updated with new data
    setTimeout(() => {
      this.selectFirstBondInGrid();
    }, 100);
  }

  handleClearFilters() {
    // Clear all filter input values
    this.cusipFilterValue = '';
    this.referenceFilterValue = '';
    this.issuerFilterValue = '';
    this.counterpartyFilterValue = '';
    
    // Clear the bonds filter criteria
    this.bondsFilterCriteria = '';
    
    // Clear current selection to trigger new selection when data loads
    this.selectedBondId = null;
    this.selectedBondCusip = null;
    this.bidsFilterCriteria = 'BOND_ID==null';
    this.bidAuditHistoryFilterCriteria = 'BOND_ID==null';
    
    // Use setTimeout to ensure the grid has updated with new data
    setTimeout(() => {
      this.selectFirstBondInGrid();
    }, 100);
  }

  handleCusipFilterApply() {
    const value = this.cusipFilterValue;
    
    // Update the bonds filter criteria based on CUSIP input
    if (value && value.trim()) {
      this.bondsFilterCriteria = `CUSIP == "${value.trim().toUpperCase()}"`;
      
      // Clear current selection to trigger new selection when data loads
      this.selectedBondId = null;
      this.selectedBondCusip = null;
      this.bidsFilterCriteria = 'BOND_ID==null';
      this.bidAuditHistoryFilterCriteria = 'BOND_ID==null';
      
      // Use setTimeout to ensure the grid has updated with new data
      setTimeout(() => {
        this.selectFirstBondInGrid();
      }, 100);
    } else {
      this.bondsFilterCriteria = '';
    }
  }

  handleReferenceFilterApply() {
    const value = this.referenceFilterValue;
    
    // Update the bonds filter criteria based on Reference # input
    if (value && value.trim()) {
      this.bondsFilterCriteria = `BOND_ID == "${value.trim()}"`;
      
      // Clear current selection to trigger new selection when data loads
      this.selectedBondId = null;
      this.selectedBondCusip = null;
      this.bidsFilterCriteria = 'BOND_ID==null';
      this.bidAuditHistoryFilterCriteria = 'BOND_ID==null';
      
      // Use setTimeout to ensure the grid has updated with new data
      setTimeout(() => {
        this.selectFirstBondInGrid();
      }, 100);
    } else {
      this.bondsFilterCriteria = '';
    }
  }

  selectFirstBondInGrid() {
    if (this.bondsGridApi) {
      const firstRow = this.bondsGridApi.getDisplayedRowAtIndex(0);
      if (firstRow && firstRow.data) {
        // Select the first row
        firstRow.setSelected(true);
        // Trigger the bond selection
        this.handleBondRowClick({ data: firstRow.data, node: firstRow });
      }
    }
  }

  handleFlyoutClosed() {
    this.bondDetailsOpen = false;
  }

  disconnectedCallback() {
    if (this.statusRefreshInterval) {
      clearInterval(this.statusRefreshInterval);
      this.statusRefreshInterval = null;
    }
    super.disconnectedCallback?.();
  }
}
